import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RegionListResponseSchema,
  type CreateDestinationRequest,
  type DestinationListQuery,
  type RegionListResponse,
  type UpdateDestinationRequest,
} from '@utazzo/shared';
import { toast } from 'sonner';

import * as api from './destinations.api';

import { apiClient } from '@/lib/api-client';

const QK = {
  list: (q: DestinationListQuery) => ['destinations', 'list', q] as const,
  detail: (id: string) => ['destinations', 'detail', id] as const,
  regions: () => ['regions', 'list'] as const,
};

export const useDestinationsList = (query: DestinationListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listDestinations(query),
    staleTime: 30_000,
  });

export const useDestination = (id: string | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => api.getDestination(id!),
    enabled: !!id,
  });

const extractMessage = (err: unknown): string | undefined => {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const m = e.response?.data?.message;
  if (Array.isArray(m)) return m.join('; ');
  return m ?? e.message;
};

export const useCreateDestination = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDestinationRequest) => api.createDestination(payload),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['destinations', 'list'] });
      toast.success(`Destination ${d.name} created`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create destination'),
  });
};

export const useUpdateDestination = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDestinationRequest }) =>
      api.updateDestination(id, payload),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['destinations', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(d.id) });
      toast.success('Destination updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update destination'),
  });
};

export const useDeleteDestination = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteDestination(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['destinations', 'list'] });
      toast.success('Destination deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete destination'),
  });
};

export const useBulkDeleteDestinations = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteDestinations(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['destinations', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete destinations'),
  });
};

export const useBulkSetDestinationsStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      api.bulkSetDestinationsStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['destinations', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update destination status'),
  });
};

/// Active regions used by the destination form's region picker.
export const useRegionsList = () =>
  useQuery({
    queryKey: QK.regions(),
    queryFn: async (): Promise<RegionListResponse> => {
      const { data } = await apiClient.get<unknown>('/regions', {
        params: { page: 1, pageSize: 100, isActive: true, sortDir: 'asc' },
      });
      return RegionListResponseSchema.parse(data);
    },
    staleTime: 5 * 60_000,
  });
